import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getOrderTrackingData } from "../../../../../api/trackingProviders";
import Loading from "../../../../ui/Loading";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Filter,
  PackageSearch,
  RefreshCw,
  Search,
  Truck,
  X,
  Download,
  ArrowUpDown,
  Phone,
  MapPin,
} from "lucide-react";

import {
  ordersList,
  getAgencies,
  scoring
} from "../../../../../api/auth";
import { city } from "../../../../../api/City";
/* =========================================================
   SMALL HELPERS
========================================================= */

const pageEnter = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const cardEnter = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28 } },
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function formatMoney(value, currency = "MAD") {
  const amount = Number(value || 0);
  return `${amount.toFixed(2)} ${currency}`;
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function getProductImage(order) {
  return (
    order?.raw_payload?.variants?.[0]?.variant?.product?.thumbnail ||
    order?.raw_payload?.variants?.[0]?.variant?.image?.url ||
    order?.raw_payload?.variants?.[0]?.variant?.product?.images?.[0]?.url ||
    null
  );
}

function getItemsFromOrder(order) {
  const variants = order?.raw_payload?.variants || [];

  if (!variants.length) {
    return [
      {
        name: order?.produit || "Product",
        quantity: 1,
        unitPrice: Number(order?.total_amount || 0),
        sku: null,
      },
    ];
  }

  return variants.map((item) => ({
    name:
      item?.variant?.product?.name ||
      item?.variant?.name ||
      order?.produit ||
      "Product",
    quantity: Number(item?.quantity || 1),
    unitPrice: Number(item?.price || 0),
    sku: item?.variant?.sku || null,
    variantName:
      Object.entries(item?.variant?.variations || {})
        .map(([k, v]) => `${k}: ${v}`)
        .join(" • ") || order?.variant || "",
  }));
}

function getTotalQuantity(order) {
  const items = getItemsFromOrder(order);
  return items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
}

function extractTrackingNumber(order) {
  return (
    order?.delivery_tracking_id ||
    order?.raw_payload?.shipping?.tracking_number ||
    null
  );
}

function getCustomerFullName(order) {
  return (
    order?.client_name ||
    order?.raw_payload?.shipping?.address?.full_name ||
    order?.raw_payload?.customer?.full_name ||
    "Unknown customer"
  );
}

function getOrderAddress(order) {
  return (
    order?.shipping_address ||
    order?.raw_payload?.shipping?.address?.address ||
    order?.raw_payload?.shipping?.address?.line1 ||
    order?.raw_payload?.customer?.address ||
    ""
  );
}
function getOrderCityId(order) {
  return (
    order?.shipping_city ||
    order?.raw_payload?.shipping?.address?.city ||
    order?.raw_payload?.customer?.city ||
    ""
  );
}

function getPaymentMethod(order) {
  return (
    order?.raw_payload?.payment?.payload?.gateway ||
    (order?.is_cod ? "cod" : "unknown")
  );
}

function mapPaymentStatus(status) {
  const value = normalizeText(status);

  if (["paid", "success", "completed"].includes(value)) return "paid";
  if (["cod"].includes(value)) return "cod";
  if (["unpaid", "pending"].includes(value)) return "unpaid";
  if (["failed", "refunded"].includes(value)) return value;

  return value || "unknown";
}

function mapStatus(localStatus, shippingStatus) {
  const local = normalizeText(localStatus);
  const shipping = normalizeText(shippingStatus);

  if (shipping === "delivered") return "Delivered";
  if (shipping === "returned") return "Returned";
  if (shipping === "cancelled") return "Cancelled";
  if (shipping === "failed_delivery") return "Failed delivery";
  if (shipping === "refused") return "Refused";
  if (shipping === "shipped") return "Shipped";
  if (shipping === "out_for_delivery") return "Out for delivery";

  if (local === "confirmed") return "Confirmed";
  if (local === "processing") return "Processing";
  if (local === "packed") return "Packed";
  if (local === "cancelled_by_client" || local === "cancelled_by_admin")
    return "Cancelled";
  if (local === "rejected") return "Refused";
  if (local === "new") return "Pending";
  if (local === "pending_confirmation") return "Pending";
  if (local === "no_answer") return "Pending";
  if (local === "call_back_later") return "Pending";

  if (shipping === "pending" || !shipping) return "Pending";

  return "Pending";
}

function buildTimeline(order) {
  const entries = [];

  if (order?.created_at) {
    entries.push({
      status: "Created",
      date: order.created_at,
      note: "Order imported into system",
    });
  }

  if (order?.confirmed_at) {
    entries.push({
      status: "Confirmed",
      date: order.confirmed_at,
      note: "Order confirmed",
    });
  }

  if (order?.shipped_at) {
    entries.push({
      status: "Shipped",
      date: order.shipped_at,
      note: "Shipment created and handed to carrier",
    });
  }

  if (order?.delivered_at) {
    entries.push({
      status: "Delivered",
      date: order.delivered_at,
      note: "Delivered successfully",
    });
  }

  if (order?.cancelled_at) {
    entries.push({
      status: "Cancelled",
      date: order.cancelled_at,
      note: "Order cancelled",
    });
  }

  if (!entries.length) {
    entries.push({
      status: mapStatus(order?.local_status, order?.shipping_status),
      date: order?.updated_at || order?.created_at,
      note: "Latest order state",
    });
  }

  return entries.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

function normalizeOrder(raw, agenciesMap, citiesMap = {}) {
  const trackingNumber = extractTrackingNumber(raw);
  const carrier =
    agenciesMap?.[raw?.delivery_agency_id]?.name || raw?.delivery_agency_id || "—";

  const cityId = getOrderCityId(raw);
  const cityName = citiesMap?.[String(cityId)] || cityId || "";

  return {
    ...raw,
    uiId: raw.id,
    orderCode:
      raw?.raw_payload?.ref ||
      raw?.external_order_id ||
      raw?.id?.slice(0, 8) ||
      raw?.id,
    customerName: getCustomerFullName(raw),
    phone: raw?.client_phone || raw?.raw_payload?.shipping?.address?.phone || "—",
    alternatePhone: raw?.raw_payload?.customer?.phone || "",
    city: cityName,          
    cityId: cityId,
    address: getOrderAddress(raw),
    postalCode:
      raw?.postal_code ||
      raw?.raw_payload?.shipping?.address?.zip_code ||
      "",
    region:
      raw?.raw_payload?.shipping?.address?.region ||
      raw?.raw_payload?.customer?.region ||
      "",
    product: raw?.produit || raw?.variant || "Product",
    items: getItemsFromOrder(raw),
    quantity: getTotalQuantity(raw),
    totalPrice: Number(raw?.total_amount || 0),
    currency: raw?.currency || raw?.raw_payload?.currency || "MAD",
    paymentMethod: getPaymentMethod(raw),
    paymentStatus: mapPaymentStatus(raw?.payment_status),
    status: mapStatus(raw?.local_status, raw?.shipping_status),
    trackingNumber,
    carrier,
    createdAt: raw?.created_at,
    updatedAt: raw?.updated_at,
    notes: raw?.note || raw?.raw_payload?.notes || "",
    internalAdminNotes: raw?.internal_note || "",
    image: getProductImage(raw),
    timeline: buildTimeline(raw),
    shippingStatus: raw?.shipping_status || "—",
  };
}

function exportOrdersToCSV(rows) {
  const headers = [
    "Order ID",
    "Customer",
    "Phone",
    "Product",
    "Quantity",
    "Total",
    "Status",
    "Shipping Status",
    "Payment Status",
    "Tracking Number",
    "Carrier",
    "City",
    "Address",
    "Created At",
    "Updated At",
  ];

  const body = rows.map((row) => [
    row.orderCode,
    row.customerName,
    row.phone,
    row.product,
    row.quantity,
    row.totalPrice,
    row.status,
    row.shippingStatus || "",
    row.paymentStatus,
    row.trackingNumber || "",
    row.carrier || "",
    row.city || "",
    row.address || "",
    row.createdAt || "",
    row.updatedAt || "",
  ]);

  const csv = [headers, ...body]
    .map((line) =>
      line
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "all-orders.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* =========================================================
   STATUS BADGES
========================================================= */

function OrderStatusBadge({ status }) {
  const styles = {
    Pending: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30",
    Confirmed: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30",
    Processing: "bg-blue-50 dark:bg-indigo-500/10 text-blue-700 dark:text-indigo-400 border-blue-200 dark:border-indigo-500/30",
    Packed: "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/30",
    Shipped: "bg-cyan-50 dark:bg-indigo-500/10 text-cyan-700 dark:text-indigo-400 border-cyan-200 dark:border-indigo-500/30",
    "Out for delivery": "bg-sky-50 dark:bg-indigo-500/10 text-sky-700 dark:text-indigo-400 border-sky-200 dark:border-indigo-500/30",
    Delivered: "bg-green-50 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 border-green-200 dark:border-emerald-500/30",
    Returned: "bg-orange-50 dark:bg-amber-500/10 text-orange-700 dark:text-amber-400 border-orange-200 dark:border-amber-500/30",
    Cancelled: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30",
    Refused: "bg-red-50 dark:bg-rose-500/10 text-red-700 dark:text-rose-400 border-red-200 dark:border-rose-500/30",
    "Failed delivery": "bg-fuchsia-50 dark:bg-rose-500/10 text-fuchsia-700 dark:text-rose-400 border-fuchsia-200 dark:border-rose-500/30",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        styles[status] || "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800"
      )}
    >
      {status}
    </span>
  );
}

function PaymentStatusBadge({ status }) {
  const styles = {
    paid: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30",
    unpaid: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30",
    cod: "bg-violet-50 dark:bg-indigo-500/10 text-violet-700 dark:text-indigo-400 border-violet-200 dark:border-indigo-500/30",
    refunded: "bg-orange-50 dark:bg-amber-500/10 text-orange-700 dark:text-amber-400 border-orange-200 dark:border-amber-500/30",
    failed: "bg-red-50 dark:bg-rose-500/10 text-red-700 dark:text-rose-400 border-red-200 dark:border-rose-500/30",
    unknown: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold uppercase",
        styles[status] || styles.unknown
      )}
    >
      {status}
    </span>
  );
}

/* =========================================================
   TRACKING
========================================================= */


async function fetchTrackingForOrder(order) {
  return await getOrderTrackingData(order);
}


function normalizePhoneForScoring(phone) {
  if (!phone) return "";
  let cleaned = String(phone).replace(/\D/g, "");

  if (cleaned.startsWith("0")) {
    cleaned = cleaned.slice(1);
  }

  if (cleaned.startsWith("212")) {
    cleaned = cleaned.slice(3);
  }

  return cleaned;
}

function getScoreVisual(scoreData) {
  if (!scoreData || scoreData.isNewClient) {
    return {
      label: "Nouveau client",
      className: "bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-300 dark:border-slate-700",
    };
  }

  if (scoreData.category === "BLACKLIST") {
    return {
      label: `Blacklist - ${Math.round(Number(scoreData.score ?? 0))}/100`,
      className: "bg-black text-white border-black",
    };
  }

  const score = Number(scoreData.score ?? 0);

  if (score <= 24) {
    return {
      label: `Black - ${Math.round(score)}/100`,
      className: "bg-black text-white border-black",
    };
  }

  if (score <= 49) {
    return {
      label: `Red - ${Math.round(score)}/100`,
      className: "bg-red-100 dark:bg-rose-500/10 text-red-700 dark:text-rose-400 border-red-300 dark:border-rose-500/30",
    };
  }

  if (score <= 74) {
    return {
      label: `Blue - ${Math.round(score)}/100`,
      className: "bg-blue-100 dark:bg-indigo-500/10 text-blue-700 dark:text-indigo-400 border-blue-300 dark:border-indigo-500/30",
    };
  }

  return {
    label: `Green - ${Math.round(score)}/100`,
    className: "bg-green-100 dark:bg-emerald-500/10 text-green-700 dark:text-emerald-400 border-green-300 dark:border-emerald-500/30",
  };
}


/* =========================================================
   PAGE
========================================================= */

const initialFilters = {
  search: "",
  orderId: "",
  customerName: "",
  phone: "",
  product: "",
  city: "",
  address: "",
  dateFrom: "",
  dateTo: "",
  status: "",
  paymentStatus: "",
  carrier: "",
  trackingNumber: "",
};

export default function AllOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientScores, setClientScores] = useState({});
const [loadingScores, setLoadingScores] = useState({});

 const loadData = useCallback(async () => {
  try {
    setLoading(true);
    setError("");

    const [ordersRes, agenciesRes, citiesRes] = await Promise.all([
      ordersList(),
      getAgencies(),
      city(),
    ]);

    const rawOrders = Array.isArray(ordersRes?.data)
      ? ordersRes.data
      : Array.isArray(ordersRes)
      ? ordersRes
      : [];

    const rawAgencies = Array.isArray(agenciesRes?.data)
      ? agenciesRes.data
      : Array.isArray(agenciesRes)
      ? agenciesRes
      : [];

    const rawCities = Array.isArray(citiesRes) ? citiesRes : [];

    const agenciesMap = rawAgencies.reduce((acc, agency) => {
      acc[agency.id] = agency;
      return acc;
    }, {});

    const citiesMap = rawCities.reduce((acc, item) => {
      acc[String(item.id)] = item.name;
      return acc;
    }, {});

    setOrders(
      rawOrders.map((order) => normalizeOrder(order, agenciesMap, citiesMap))
    );
  } catch (err) {
    console.error(err);
    setError("Failed to load orders.");
  } finally {
    setLoading(false);
  }
}, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  const summary = useMemo(() => {
    const count = (status) => orders.filter((o) => o.status === status).length;

    return {
      total: orders.length,
      pending: count("Pending"),
      shipped: count("Shipped") + count("Out for delivery"),
      delivered: count("Delivered"),
      cancelledReturned: count("Cancelled") + count("Returned") + count("Refused"),
    };
  }, [orders]);


const fetchClientScore = useCallback(async (order) => {
  try {
    const normalizedPhone = normalizePhoneForScoring(order.phone);

    if (!normalizedPhone) {
      setClientScores((prev) => ({
        ...prev,
        [order.uiId]: {
          score: null,
          category: null,
          raw: null,
          isNewClient: true,
        },
      }));
      return;
    }

    setLoadingScores((prev) => ({
      ...prev,
      [order.uiId]: true,
    }));

    const res = await scoring(normalizedPhone);
    const data = res?.data;

    setClientScores((prev) => ({
      ...prev,
      [order.uiId]: {
        score: data?.final?.score_100 ?? null,
        category: data?.final?.category?.value ?? null,
        raw: data ?? null,
        isNewClient: (data?.final?.orders_lifetime ?? 0) === 0,
      },
    }));
  } catch (err) {
    console.error("Scoring fetch error", err);

    setClientScores((prev) => ({
      ...prev,
      [order.uiId]: {
        score: null,
        category: null,
        raw: null,
        isNewClient: true,
        error: true,
      },
    }));
  } finally {
    setLoadingScores((prev) => ({
      ...prev,
      [order.uiId]: false,
    }));
  }
}, []);


  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (filters.search) {
      const q = normalizeText(filters.search);
      result = result.filter(
        (o) =>
          normalizeText(o.orderCode).includes(q) ||
          normalizeText(o.customerName).includes(q) ||
          normalizeText(o.phone).includes(q) ||
          normalizeText(o.product).includes(q) ||
          normalizeText(o.city).includes(q) ||
          normalizeText(o.address).includes(q) ||
          normalizeText(o.trackingNumber).includes(q) ||
          normalizeText(o.carrier).includes(q)
      );
    }

    if (filters.orderId) {
      const q = normalizeText(filters.orderId);
      result = result.filter((o) => normalizeText(o.orderCode).includes(q));
    }

    if (filters.customerName) {
      const q = normalizeText(filters.customerName);
      result = result.filter((o) => normalizeText(o.customerName).includes(q));
    }

    if (filters.phone) {
      const q = normalizeText(filters.phone);
      result = result.filter((o) => normalizeText(o.phone).includes(q));
    }

    if (filters.product) {
      const q = normalizeText(filters.product);
      result = result.filter((o) => normalizeText(o.product).includes(q));
    }

    if (filters.city) {
      const q = normalizeText(filters.city);
      result = result.filter((o) => normalizeText(o.city).includes(q));
    }

    if (filters.address) {
      const q = normalizeText(filters.address);
      result = result.filter((o) => normalizeText(o.address).includes(q));
    }

    if (filters.status) {
      result = result.filter(
        (o) => normalizeText(o.status) === normalizeText(filters.status)
      );
    }

    if (filters.paymentStatus) {
      result = result.filter(
        (o) =>
          normalizeText(o.paymentStatus) === normalizeText(filters.paymentStatus)
      );
    }

    if (filters.carrier) {
      const q = normalizeText(filters.carrier);
      result = result.filter((o) => normalizeText(o.carrier).includes(q));
    }

    if (filters.trackingNumber) {
      const q = normalizeText(filters.trackingNumber);
      result = result.filter((o) => normalizeText(o.trackingNumber).includes(q));
    }

    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      from.setHours(0, 0, 0, 0);
      result = result.filter((o) => new Date(o.createdAt) >= from);
    }

    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter((o) => new Date(o.createdAt) <= to);
    }

    result.sort((a, b) => {
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      if (sortConfig.key === "createdAt" || sortConfig.key === "updatedAt") {
        return (
          (new Date(a[sortConfig.key]).getTime() -
            new Date(b[sortConfig.key]).getTime()) *
          direction
        );
      }

      if (sortConfig.key === "totalPrice" || sortConfig.key === "quantity") {
        return (Number(a[sortConfig.key]) - Number(b[sortConfig.key])) * direction;
      }

      return String(a[sortConfig.key] || "").localeCompare(
        String(b[sortConfig.key] || "")
      ) * direction;
    });

    return result;
  }, [orders, filters, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));

  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, page, pageSize]);

  const currentPageIds = paginatedOrders.map((order) => order.uiId);
  const allSelectedOnPage =
    currentPageIds.length > 0 &&
    currentPageIds.every((id) => selectedIds.includes(id));

  const toggleSelectRow = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((item) => item !== id)
    );
  };

  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
      return;
    }

    setSelectedIds((prev) => [...new Set([...prev, ...currentPageIds])]);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

//   const applyTrackingUpdateToOrder = useCallback((order, trackingData) => {
//   if (!order?.uiId || !trackingData) return;

//   const nextShippingStatus =
//     trackingData?.trackingStatus ||
//     trackingData?.raw?.tracking?.ozon_status ||
//     trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.STATUT ||
//     trackingData?.raw?.tracking?.last_tracking_status_name ||
//     trackingData?.raw?.tracking?.last_tracking_status_code ||
//     order.shippingStatus ||
//     "—";

//   const nextTrackingNumber =
//     trackingData?.trackingNumber ||
//     trackingData?.raw?.tracking?.tracking_number ||
//     trackingData?.raw?.tracking?.ozon_body?.TRACKING?.["TRACKING-NUMBER"] ||
//     order.trackingNumber ||
//     null;

//   const nextUpdatedAt =
//     trackingData?.lastUpdate ||
//     trackingData?.raw?.tracking?.last_tracking_occurred_at ||
//     trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.TIME_STR ||
//     order.updatedAt;

//   setOrders((prev) =>
//     prev.map((item) =>
//       item.uiId === order.uiId
//         ? {
//             ...item,
//             shippingStatus: nextShippingStatus,
//             trackingNumber: nextTrackingNumber,
//             updatedAt: nextUpdatedAt,
//           }
//         : item
//     )
//   );

//   setSelectedOrder((prev) =>
//     prev?.uiId === order.uiId
//       ? {
//           ...prev,
//           shippingStatus: nextShippingStatus,
//           trackingNumber: nextTrackingNumber,
//           updatedAt: nextUpdatedAt,
//         }
//       : prev
//   );

//   setTrackingOrder((prev) =>
//     prev?.uiId === order.uiId
//       ? {
//           ...prev,
//           shippingStatus: nextShippingStatus,
//           trackingNumber: nextTrackingNumber,
//           updatedAt: nextUpdatedAt,
//         }
//       : prev
//   );
// }, []);


// const handleOpenTracking = async (order) => {
//   setTrackingOrder(order);
//   setTrackingLoading(true);
//   setTrackingData(null);
//   const data = await fetchTrackingForOrder(order);
//   setTrackingData(data);
//   applyTrackingUpdateToOrder(order, data);
//   setTrackingLoading(false);
// };
const applyTrackingUpdateToOrder = useCallback((order, trackingData) => {
  if (!order?.uiId || !trackingData) return;

  const forcelogParcel =
    trackingData?.raw?.parcel?.forcelog_response?.["GET-PARCEL"]?.PARCEL || {};

  const nextShippingStatus =
    trackingData?.trackingStatus ||
    trackingData?.raw?.parcel?.extracted_status ||
    forcelogParcel?.STATUS ||
    forcelogParcel?.STATUS_CODE ||
    trackingData?.raw?.tracking?.ozon_status ||
    trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.STATUT ||
    trackingData?.raw?.tracking?.last_tracking_status_name ||
    trackingData?.raw?.tracking?.last_tracking_status_code ||
    order.shippingStatus ||
    "—";

  const nextTrackingNumber =
    trackingData?.trackingNumber ||
    forcelogParcel?.TRACKING_NUMBER ||
    trackingData?.raw?.tracking?.tracking_number ||
    trackingData?.raw?.tracking?.ozon_body?.TRACKING?.["TRACKING-NUMBER"] ||
    order.trackingNumber ||
    null;

  const nextUpdatedAt =
    trackingData?.lastUpdate ||
    forcelogParcel?.CREATION_TIME ||
    trackingData?.raw?.tracking?.last_tracking_occurred_at ||
    trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.TIME_STR ||
    order.updatedAt;

  setOrders((prev) =>
    prev.map((item) =>
      item.uiId === order.uiId
        ? {
            ...item,
            shippingStatus: nextShippingStatus,
            trackingNumber: nextTrackingNumber,
            updatedAt: nextUpdatedAt,
          }
        : item
    )
  );

  setSelectedOrder((prev) =>
    prev?.uiId === order.uiId
      ? {
          ...prev,
          shippingStatus: nextShippingStatus,
          trackingNumber: nextTrackingNumber,
          updatedAt: nextUpdatedAt,
        }
      : prev
  );

  setTrackingOrder((prev) =>
    prev?.uiId === order.uiId
      ? {
          ...prev,
          shippingStatus: nextShippingStatus,
          trackingNumber: nextTrackingNumber,
          updatedAt: nextUpdatedAt,
        }
      : prev
  );
}, []);

const handleOpenTracking = async (order) => {
  setTrackingOrder(order);
  setTrackingLoading(true);
  setTrackingData(null);

  try {
    const data = await fetchTrackingForOrder(order);
    setTrackingData(data);
    applyTrackingUpdateToOrder(order, data);
  } catch (error) {
    console.error("handleOpenTracking failed:", error);
    setTrackingData({
      provider: null,
      trackingNumber: order?.trackingNumber || null,
      trackingStatus: "Unable to fetch tracking",
      lastUpdate: null,
      history: [],
      parcel: null,
      raw: null,
      error: true,
    });
  } finally {
    setTrackingLoading(false);
  }
};

  useEffect(() => {
  if (!paginatedOrders?.length) return;

  paginatedOrders.forEach((order) => {
    if (!order?.uiId) return;
    if (clientScores[order.uiId] || loadingScores[order.uiId]) return;

    fetchClientScore(order);
  });
}, [paginatedOrders, clientScores, loadingScores, fetchClientScore]);

  const uniqueCities = [...new Set(orders.map((o) => o.city).filter(Boolean))];
  const uniqueCarriers = [...new Set(orders.map((o) => o.carrier).filter(Boolean))];

  return (
    <div className="space-y-6">
      <motion.div
        variants={pageEnter}
        initial="initial"
        animate="animate"
        className="flex flex-col gap-4 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between"
      >
        <div>

          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            All Orders
          </h1>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-500">
            Track, filter, and manage all orders in one place.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>

          <button
            onClick={() => {
              const firstTrackable = filteredOrders.find((o) => o.trackingNumber);
              if (firstTrackable) {
                handleOpenTracking(firstTrackable);
              }
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-slate-700 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 dark:hover:bg-slate-600"
          >
            <Truck className="h-4 w-4" />
            Tracking
          </button>
        </div>
      </motion.div>

      <motion.div
        variants={cardEnter}
        initial="initial"
        animate="animate"
        className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500 dark:text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Filters</h2>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            icon={Search}
            placeholder="Global search"
            value={filters.search}
            onChange={(value) => setFilters((prev) => ({ ...prev, search: value }))}
          />
          <Input
            placeholder="Order ID"
            value={filters.orderId}
            onChange={(value) => setFilters((prev) => ({ ...prev, orderId: value }))}
          />
          <Input
            placeholder="Customer name"
            value={filters.customerName}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, customerName: value }))
            }
          />
          <Input
            placeholder="Phone number"
            value={filters.phone}
            onChange={(value) => setFilters((prev) => ({ ...prev, phone: value }))}
          />
          <Input
            placeholder="Product"
            value={filters.product}
            onChange={(value) => setFilters((prev) => ({ ...prev, product: value }))}
          />
          <SelectField
            value={filters.city}
            onChange={(value) => setFilters((prev) => ({ ...prev, city: value }))}
            options={uniqueCities}
            placeholder="City"
          />
          <Input
            placeholder="Address"
            value={filters.address}
            onChange={(value) => setFilters((prev) => ({ ...prev, address: value }))}
          />
          <SelectField
            value={filters.status}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            options={[
              "Pending",
              "Confirmed",
              "Processing",
              "Packed",
              "Shipped",
              "Out for delivery",
              "Delivered",
              "Returned",
              "Cancelled",
              "Refused",
              "Failed delivery",
            ]}
            placeholder="Status"
          />
          <SelectField
            value={filters.paymentStatus}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, paymentStatus: value }))
            }
            options={["paid", "unpaid", "cod", "refunded", "failed"]}
            placeholder="Payment status"
          />
          <SelectField
            value={filters.carrier}
            onChange={(value) => setFilters((prev) => ({ ...prev, carrier: value }))}
            options={uniqueCarriers}
            placeholder="Delivery company"
          />
          <Input
            placeholder="Tracking number"
            value={filters.trackingNumber}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, trackingNumber: value }))
            }
          />

          <Input
            type="date"
            value={filters.dateFrom}
            onChange={(value) => setFilters((prev) => ({ ...prev, dateFrom: value }))}
          />
          <Input
            type="date"
            value={filters.dateTo}
            onChange={(value) => setFilters((prev) => ({ ...prev, dateTo: value }))}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-500 dark:text-slate-500">
            {filteredOrders.length} filtered result
            {filteredOrders.length !== 1 ? "s" : ""}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => exportOrdersToCSV(filteredOrders)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>

            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
              Clear filters
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        <SummaryCard title="Total Orders" value={summary.total} />
        <SummaryCard title="Pending" value={summary.pending} />
        <SummaryCard title="Shipped" value={summary.shipped} />
        <SummaryCard title="Delivered" value={summary.delivered} />
        <SummaryCard title="Cancelled / Returned" value={summary.cancelledReturned} />
      </div>

      <motion.div
        variants={cardEnter}
        initial="initial"
        animate="animate"
        className="overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
      >
          
{loading ? (
  <div className="py-14">
    <Loading text="Loading orders..." variant="dots" />
  </div>
) : error ? (
  <ErrorState message={error} onRetry={loadData} />
) : !filteredOrders.length ? (
  <EmptyState />
) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="min-w-full">
                <thead className="sticky top-0 z-10 bg-slate-50/95 dark:bg-slate-800/60 backdrop-blur">
                  <tr className="border-b border-slate-200 dark:border-slate-800">
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelectedOnPage}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-slate-300 dark:border-slate-700"
                      />
                    </th>

                    <TableHead label="Order ID" onClick={() => toggleSort("orderCode")} />
                    <TableHead label="Customer" onClick={() => toggleSort("customerName")} />
                    <TableHead label="Phone" />
                    <TableHead label="Product / Items" />
                    <TableHead label="Scoring" />
                    <TableHead label="Total Price" onClick={() => toggleSort("totalPrice")} />
                    <TableHead label="Status" onClick={() => toggleSort("status")} />
                    <TableHead label="Shipping Status" onClick={() => toggleSort("shippingStatus")} />
                    <TableHead label="Payment" onClick={() => toggleSort("paymentStatus")} />
                    <TableHead label="Tracking Number" />
                    <TableHead label="Delivery Company" />
                    <TableHead label="Created At" onClick={() => toggleSort("createdAt")} />
                    <TableHead label="Updated At" onClick={() => toggleSort("updatedAt")} />
                    <TableHead label="Actions" />
                  </tr>
                </thead>

                <tbody>
                  {paginatedOrders.map((order, index) => (
                    <motion.tr
                      key={order.uiId}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      className="border-b border-slate-100 dark:border-slate-800 transition hover:bg-slate-50/70 dark:hover:bg-slate-800/70"
                    >
                      <td className="px-4 py-4 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(order.uiId)}
                          onChange={(e) =>
                            toggleSelectRow(order.uiId, e.target.checked)
                          }
                          className="h-4 w-4 rounded border-slate-300 dark:border-slate-700"
                        />
                      </td>

                      <td className="px-4 py-4 align-top text-sm font-semibold text-slate-900 dark:text-slate-100">
                        #{order.orderCode}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {order.customerName}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">{order.city || "—"}</div>
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-300">
                        {order.phone}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="max-w-[240px]">
                          <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                            {order.product}
                          </div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                            {order.items.length} item{order.items.length > 1 ? "s" : ""}
                          </div>
                        </div>
                      </td>

<td className="px-4 py-4 align-top">
{loadingScores[order.uiId] ? (
  <div className="py-1">
    <Loading text="" size="sm" className="items-start" />
  </div>
) : (
    (() => {
      const visual = getScoreVisual(clientScores[order.uiId]);

      return (
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
            visual.className
          )}
        >
          {visual.label}
        </span>
      );
    })()
  )}
</td>

                      <td className="px-4 py-4 align-top text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {formatMoney(order.totalPrice, order.currency)}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <OrderStatusBadge status={order.status} />
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-300">
                        {order.shippingStatus || "—"}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <PaymentStatusBadge status={order.paymentStatus} />
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-300">
                        {order.trackingNumber || "—"}
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-300">
                        {order.carrier || "—"}
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-500 dark:text-slate-500">
                        {formatDate(order.createdAt)}
                      </td>

                      <td className="px-4 py-4 align-top text-sm text-slate-500 dark:text-slate-500">
                        {formatDate(order.updatedAt)}
                      </td>

                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setSelectedOrder(order)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>

                          <button
                            onClick={() => handleOpenTracking(order)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Truck className="h-3.5 w-3.5" />
                            Track
                          </button>

                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-3 p-4 lg:hidden">
              {paginatedOrders.map((order) => (
                <motion.div
                  key={order.uiId}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        #{order.orderCode}
                      </div>
                      <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        {order.customerName}
                      </div>
                    </div>
                    <OrderStatusBadge status={order.status} />
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {order.phone}
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {order.city || "—"}
                    </div>
                    <div>{order.product}</div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">
                      {formatMoney(order.totalPrice, order.currency)}
                    </div>
                    <PaymentStatusBadge status={order.paymentStatus} />
                  </div>
<div>
{loadingScores[order.uiId] ? (
  <div className="py-1">
    <Loading text="Loading scoring..." size="sm" />
  </div>
) : (
    (() => {
      const visual = getScoreVisual(clientScores[order.uiId]);

      return (
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
            visual.className
          )}
        >
          {visual.label}
        </span>
      );
    })()
  )}
</div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleOpenTracking(order)}
                      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300"
                    >
                      Track
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-200 dark:border-slate-800 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span>Show</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-3 py-2 text-sm outline-none"
                >
                  <option value={10}>10 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                </select>
                <span>
                  {filteredOrders.length} total result
                  {filteredOrders.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                  Page {page} / {totalPages}
                </div>

                <button
                  onClick={() =>
                    setPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 transition hover:bg-slate-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      <AnimatePresence>
        {selectedOrder && (
<OrderDetailsModal
  order={selectedOrder}
  trackingData={trackingOrder?.uiId === selectedOrder?.uiId ? trackingData : null}
  scoreData={clientScores[selectedOrder.uiId]}
  scoreLoading={loadingScores[selectedOrder.uiId]}
  onClose={() => setSelectedOrder(null)}
  onTrack={() => handleOpenTracking(selectedOrder)}
/>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {trackingOrder && (
          <TrackingModal
            order={trackingOrder}
            trackingData={trackingData}
            loading={trackingLoading}
            onClose={() => {
              setTrackingOrder(null);
              setTrackingData(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* =========================================================
   UI PIECES
========================================================= */

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  icon: Icon,
}) {
  return (
    <div className="relative">
      {Icon ? (
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
      ) : null}

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 outline-none transition placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-slate-300 dark:focus:border-slate-600 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-800",
          Icon && "pl-10"
        )}
      />
    </div>
  );
}

function SelectField({ value, onChange, options = [], placeholder }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 pr-10 text-sm text-slate-700 dark:text-slate-300 outline-none transition focus:border-slate-300 dark:focus:border-slate-600 focus:ring-4 focus:ring-slate-100 dark:focus:ring-slate-800"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={String(option)} value={String(option)}>
            {String(option)}
          </option>
        ))}
      </select>

      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      transition={{ duration: 0.18 }}
      className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm"
    >
      <div className="text-sm font-medium text-slate-500 dark:text-slate-500">{title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
    </motion.div>
  );
}

function TableHead({ label, onClick }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-500">
      {onClick ? (
        <button
          onClick={onClick}
          className="inline-flex items-center gap-1 transition hover:text-slate-700 dark:hover:text-slate-300"
        >
          {label}
          <ArrowUpDown className="h-3.5 w-3.5" />
        </button>
      ) : (
        label
      )}
    </th>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-500">
        <PackageSearch className="h-8 w-8" />
      </div>
      <h3 className="mt-5 text-lg font-semibold text-slate-900 dark:text-slate-100">No orders found</h3>
      <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-500">
        No orders match your current filters.
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="p-6">
      <div className="rounded-2xl border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-5">
        <div className="text-sm font-medium text-rose-700 dark:text-rose-400">{message}</div>
        <button
          onClick={onRetry}
          className="mt-4 rounded-xl border border-rose-200 dark:border-rose-500/30 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-rose-700 dark:text-rose-400 transition hover:bg-rose-100 dark:hover:bg-rose-500/20"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   DETAILS MODAL
========================================================= */

function OrderDetailsModal({ order, trackingData, scoreData, scoreLoading, onClose, onTrack }) {
  console.log("order" , order)
  console.log("onTrack" , onTrack)
      return (
    <>
      <motion.div
        className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.2 }}
      >
        <div className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                Order Details
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                Full customer, payment, shipping, and timeline information.
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl border border-slate-200 dark:border-slate-800 p-2 text-slate-500 dark:text-slate-500 transition hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[calc(92vh-80px)] overflow-y-auto p-6">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="xl:col-span-2 space-y-6">
                <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-5">
                  <div className="flex flex-col gap-5 lg:flex-row">
                    <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800">
                      {order.image ? (
                        <img
                          src={order.image}
                          alt={order.product}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-400 dark:text-slate-500">
                          <PackageSearch className="h-8 w-8" />
                        </div>
                      )}
                    </div>



                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          #{order.orderCode}
                        </h3>
                        <OrderStatusBadge status={order.status} />
                        <PaymentStatusBadge status={order.paymentStatus} />
                      </div>

                      <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                        {order.product}
                      </div>

                    <div className="mt-3">
{scoreLoading ? (
  <div className="py-1">
    <Loading text="Loading scoring..." size="sm" />
  </div>
) : (
    (() => {
      const visual = getScoreVisual(scoreData);

      return (
        <span
          className={cn(
            "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
            visual.className
          )}
        >
          {visual.label}
        </span>
      );
    })()
  )}
</div>

                      <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-slate-600 dark:text-slate-400 md:grid-cols-2">
                        <InfoField label="Customer" value={order.customerName} />
                        <InfoField label="Phone" value={order.phone} />
                        <InfoField label="Alternate phone" value={order.alternatePhone || "—"} />
                        <InfoField label="Payment method" value={order.paymentMethod} />
                        <InfoField
                          label="Total price"
                          value={formatMoney(order.totalPrice, order.currency)}
                        />
                        <InfoField label="Quantity" value={order.quantity} />
                      </div>

                      <div className="mt-5 flex flex-wrap gap-2">
                        <button
                          onClick={onTrack}
                          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 dark:hover:bg-slate-600"
                        >
                          <Truck className="h-4 w-4" />
                          Open tracking
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <SectionCard title="Shipping Information">
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
    <InfoField label="City" value={order.city || "—"} />
    <InfoField label="Region" value={order.region || "—"} />
    <InfoField label="Postal code" value={order.postalCode || "—"} />
    <InfoField label="Carrier" value={order.carrier || "—"} />
    {/* <InfoField
      label="Tracking number"
      value={
        trackingData?.trackingNumber ||
        trackingData?.raw?.tracking?.tracking_number ||
        order.trackingNumber ||
        "—"
      }
    />
    <InfoField
      label="Tracking status"
      value={
        trackingData?.trackingStatus ||
        trackingData?.raw?.tracking?.ozon_status ||
        trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.STATUT ||
        trackingData?.raw?.tracking?.last_tracking_status_name ||
        trackingData?.raw?.tracking?.last_tracking_status_code ||
        trackingData?.history?.[trackingData.history.length - 1]?.status ||
        order.shippingStatus ||
        "—"
      }
    /> */}

<InfoField
  label="Tracking number"
  value={
   trackingData?.trackingNumber ||
        trackingData?.raw?.tracking?.tracking_number ||
        order.trackingNumber ||
        "—"
  }
/>

<InfoField
  label="Tracking status"
  value={
 trackingData?.trackingStatus ||
        trackingData?.raw?.tracking?.ozon_status ||
        trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.STATUT ||
        trackingData?.raw?.tracking?.last_tracking_status_name ||
        trackingData?.raw?.tracking?.last_tracking_status_code ||
        trackingData?.history?.[trackingData.history.length - 1]?.status ||
        "No tracking data"
  }
/>


    <InfoField label="Address" value={order.address || "—"} />
  </div>
</SectionCard>

                <SectionCard title="Ordered Items">
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div
                        key={`${item.name}-${index}`}
                        className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {item.name}
                            </div>
                            <div className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                              {item.variantName || "Default variant"}
                            </div>
                            {item.sku ? (
                              <div className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                                SKU: {item.sku}
                              </div>
                            ) : null}
                          </div>

                          <div className="text-sm text-slate-600 dark:text-slate-400">
                            Qty: <span className="font-semibold">{item.quantity}</span>{" "}
                            • Unit:{" "}
                            <span className="font-semibold">
                              {formatMoney(item.unitPrice, order.currency)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <SectionCard title="Notes">
                  <div className="space-y-4">
                    <InfoField label="Customer notes" value={order.notes || "—"} />
                    <InfoField
                      label="Internal admin notes"
                      value={order.internalAdminNotes || "—"}
                    />
                  </div>
                </SectionCard>
              </div>

              <div className="space-y-6">
                <SectionCard title="Order Metadata">
                  <div className="space-y-3">
                    <InfoField label="Platform" value={order.platform || "—"} />
                    <InfoField label="External order ID" value={order.external_order_id || "—"} />
                    <InfoField label="Created at" value={formatDate(order.createdAt)} />
                    <InfoField label="Updated at" value={formatDate(order.updatedAt)} />
                    <InfoField label="Store Teqa ID" value={order.store_teqa_id || "—"} />
                    <InfoField
                      label="Source connection"
                      value={order.source_connection || "—"}
                    />
                  </div>
                </SectionCard>

                <SectionCard title="Order Timeline">
                  <div className="space-y-4">
                    {order.timeline.map((item, index) => (
                      
                      <div key={`${item.status}-${index}`} className="relative pl-6">
                        {index !== order.timeline.length - 1 && (
                          <span className="absolute left-[9px] top-5 h-full w-px bg-slate-200 dark:bg-slate-800" />
                        )}
                        <span className="absolute left-0 top-1.5 h-5 w-5 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" />
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {item.status}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                          {formatDate(item.date)}
                        </div>
                        <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                          {item.note || "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-5">
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-slate-700 dark:text-slate-300 break-words">{value}</div>
    </div>
  );
}

/* =========================================================
   TRACKING MODAL
========================================================= */

function TrackingModal({ order, trackingData, loading, onClose }) {
  console.log("TRACKING MODAL DATA =", trackingData);

  const fallbackHistory =
    order?.timeline?.map((item) => ({
      status: item.status,
      date: item.date,
      location: order.city || "—",
    })) || [];

  const history =
    Array.isArray(trackingData?.history) && trackingData.history.length
      ? trackingData.history
      : fallbackHistory;

  return (
    <>
      <motion.div
        className="fixed inset-0 z-50 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.2 }}
      >
        <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Tracking</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
                Shipment tracking, carrier info, and parcel history.
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-xl border border-slate-200 dark:border-slate-800 p-2 text-slate-500 dark:text-slate-500 transition hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[calc(90vh-80px)] overflow-y-auto p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SectionCard title="Parcel Info">
                <div className="space-y-3">
                  <InfoField label="Order" value={`#${order.orderCode}`} />
                  <InfoField label="Customer" value={order.customerName} />
                  <InfoField label="Carrier" value={order.carrier || "—"} />
                  <InfoField
                    label="Tracking number"
                    value={order.trackingNumber || "—"}
                  />
<InfoField
  label="Tracking status"
  value={
    loading
      ? "Loading..."
      : trackingData?.trackingStatus ||
        trackingData?.raw?.tracking?.ozon_status ||
        trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.STATUT ||
        history?.[history.length - 1]?.status ||
        "No tracking data"
  }
/>
<InfoField
  label="Last update"
  value={
     
       formatDate(
          trackingData?.lastUpdate ||
          trackingData?.raw?.tracking?.last_tracking_occurred_at ||
          trackingData?.raw?.tracking?.TRACKING?.LAST_TRACKING?.TIME_STR ||
          trackingData?.raw?.tracking?.ozon_body?.TRACKING?.LAST_TRACKING?.TIME_STR ||
          order.updatedAt
        )
  }
/>
                </div>
              </SectionCard>

              <SectionCard title="Delivery Address">
                <div className="space-y-3">
                  <InfoField label="Phone" value={order.phone || "—"} />
                  <InfoField label="City" value={order.city || "—"} />
                  <InfoField label="Address" value={order.address || "—"} />
                </div>
              </SectionCard>
            </div>

            <div className="mt-6 rounded-3xl border border-slate-200 dark:border-slate-800 p-5">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Tracking History
              </h3>

{loading ? (
  <div className="py-8">
    <Loading text="Loading tracking updates..." variant="dots" />
  </div>
) : history.length ? (
                <div className="mt-5 space-y-4">
                  {history.map((event, index) => (
                    <div key={index} className="relative pl-6">
                      {index !== history.length - 1 && (
                        <span className="absolute left-[9px] top-5 h-full w-px bg-slate-200 dark:bg-slate-800" />
                      )}
                      <span className="absolute left-0 top-1.5 h-5 w-5 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" />
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {event?.status ||
                          event?.label ||
                          event?.state ||
                          "Tracking update"}
                          {console.log("history" ,history)}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                        {formatDate(
                          event?.date ||
                            event?.created_at ||
                            event?.updated_at ||
                            event?.timestamp
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                        {event?.location ||
                          event?.city ||
                          event?.hub ||
                          event?.note ||
                          "—"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-sm text-slate-500 dark:text-slate-500">
                  No tracking history available for this parcel yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}
